"use client"

import React, { useEffect, useState, useRef } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { 
  ArrowDown, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Lock, Info, X, Calendar, Waves, Wind, Clock, Navigation
} from "lucide-react"
import { usePreferences } from "@/contexts/preferences-context"
import { convertWaveHeight, convertWindSpeed, convertTemperatureString } from "@/lib/unit-conversions"
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip as RechartsTooltip, ReferenceLine } from "recharts"
import { useAuth } from "@/lib/auth/context"
import { useProfile } from "@/hooks/use-profile"
import Link from "next/link"
import { ForecastMapVisualization } from "@/components/forecast-map-visualization"
import { createClient } from "@/lib/supabase/client"
import { usePlatform, triggerHaptic } from "@/hooks/use-platform"
import { cn } from "@/lib/utils"
import { ForecastInfoPopup } from "@/components/forecast-info-popup"
import { TideGraph } from "@/components/tide-graph"

interface ForecastOverviewProps {
  spotId: string
  usedFallback?: boolean
  activityType?: "surfing" | "kitesurfing" | "both" | null
  onJoinClick?: () => void
}

interface HourlyForecast {
  time: string
  timestamp?: string
  hour: number
  waveHeight: number
  waveHeightDisplay: string
  swellHeight?: number
  totalWaveHeight?: number
  windSpeed: number
  windSpeedDisplay: string
  windGust?: number
  windDirection: string
  windDirectionDeg: number
  wavePeriod: number
  waveDirection: string
  waveDirectionDeg: number
  temperature: number
  temperatureDisplay: string
  weatherCode: number
  weatherCondition: string
  weatherIcon: string
  rating: number
  quality: string
  qualityColor: string
  tideHeight?: number
  tideTrend?: 'rising' | 'falling' | 'stable'
  kiteQuality?: string
  kiteQualityColor?: string
  kiteRating?: number
}

interface ForecastDay {
  date: string
  waveHeight: string
  quality: string
  windSpeed: string
  windDirection: string
  temperature: number
  temperatureDisplay: string
  weatherCondition: string
  weatherIcon: string
  tides?: Array<{ time: string; value: number; type: "high" | "low" }>
  hourly: HourlyForecast[]
  kiteQuality?: string
  kiteQualityColor?: string
}

// Helper Functions (Same as WeatherDashboard)
const normalizeQuality = (quality: string): "epic" | "good" | "fair" | "poor" | "flat" | "extreme" => {
  const q = (quality || "").toLowerCase()
  if (q === "excellent") return "epic"
  if (q === "very good" || q === "very-good" || q === "vg") return "good"
  if (q === "ok" || q === "average") return "fair"
  if (q === "bad") return "poor"
  if (q === "flat") return "flat"
  if (q === "extreme") return "extreme"
  if (q === "epic" || q === "good" || q === "fair" || q === "poor") return q as any
  return "poor"
}

const getQualityColor = (quality: string) => {
  switch (normalizeQuality(quality)) {
    case "epic": return "#10b981"
    case "good": return "#3b82f6"
    case "fair": return "#f59e0b"
    case "poor":
    case "flat": return "#ef4444"
    case "extreme": return "#7c2d12"
    default: return "#9ca3af"
  }
}

const getAverageQuality = (hours: HourlyForecast[]): string => {
  const qualityScores: Record<string, number> = { epic: 5, good: 4, fair: 3, poor: 2, flat: 1, extreme: 1 }
  const avgScore = hours.reduce((sum, h) => sum + (qualityScores[normalizeQuality(h.quality)] || 1), 0) / (hours.length || 1)
  if (avgScore >= 4.5) return "epic"
  if (avgScore >= 3.5) return "good"
  if (avgScore >= 2.5) return "fair"
  if (avgScore >= 1.5) return "poor"
  return "flat"
}

const getAverageKiteQuality = (hours: HourlyForecast[]): string => {
  const qualityScores: Record<string, number> = { epic: 5, good: 4, fair: 3, poor: 2, extreme: 1, flat: 1 }
  const avgScore = hours.reduce((sum, h) => sum + (qualityScores[normalizeQuality(h.kiteQuality || 'poor')] || 1), 0) / (hours.length || 1)
  if (avgScore >= 4.5) return "epic"
  if (avgScore >= 3.5) return "good"
  if (avgScore >= 2.5) return "fair"
  if (avgScore >= 1.5) return "poor"
  return "poor"
}

const getAverageWindDirection = (hours: HourlyForecast[]): number => {
  if (hours.length === 0) return 0
  return hours.reduce((sum, h) => sum + h.windDirectionDeg, 0) / hours.length
}

const getAveragePeriod = (hours: HourlyForecast[]): number => {
  if (hours.length === 0) return 0
  return Math.round(hours.reduce((sum, h) => sum + h.wavePeriod, 0) / hours.length)
}

const getAverageWaveDirection = (hours: HourlyForecast[]): string => {
  if (hours.length === 0) return "N"
  const avgDeg = hours.reduce((sum, h) => sum + h.waveDirectionDeg, 0) / hours.length
  const directions = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"]
  return directions[Math.round(avgDeg / 22.5) % 16]
}

const getDangerousKiteLabel = (hour: HourlyForecast): string => {
  if (hour.kiteQuality?.toLowerCase() !== 'extreme') return (hour.kiteQuality || 'POOR').toUpperCase()
  if (hour.windGust) {
    const gustDifferenceKnots = (hour.windGust - hour.windSpeed) * 0.868976
    if (gustDifferenceKnots >= 10) return "EXTREME GUSTS"
  }
  if (hour.windSpeed > 35) return "EXTREME WINDS"
  if (hour.windSpeed > 30) return "VERY STRONG"
  if (hour.windSpeed > 24) return "TOO STRONG"
  return "OFFSHORE"
}

const getTimePeriods = (hourly: HourlyForecast[]) => {
  const morning = hourly.filter((h) => h.hour >= 7 && h.hour < 11)
  const midday = hourly.filter((h) => h.hour >= 11 && h.hour < 15)
  const afternoon = hourly.filter((h) => h.hour >= 15 && h.hour < 19)
  const evening = hourly.filter((h) => h.hour >= 19 && h.hour <= 22)

  return [
    { label: "Morning", hours: morning, quality: getAverageQuality(morning), kiteQuality: getAverageKiteQuality(morning), windDir: getAverageWindDirection(morning), period: getAveragePeriod(morning), waveDir: getAverageWaveDirection(morning) },
    { label: "Midday", hours: midday, quality: getAverageQuality(midday), kiteQuality: getAverageKiteQuality(midday), windDir: getAverageWindDirection(midday), period: getAveragePeriod(midday), waveDir: getAverageWaveDirection(midday) },
    { label: "Afternoon", hours: afternoon, quality: getAverageQuality(afternoon), kiteQuality: getAverageKiteQuality(afternoon), windDir: getAverageWindDirection(afternoon), period: getAveragePeriod(afternoon), waveDir: getAverageWaveDirection(afternoon) },
    { label: "Evening", hours: evening, quality: getAverageQuality(evening), kiteQuality: getAverageKiteQuality(evening), windDir: getAverageWindDirection(evening), period: getAveragePeriod(evening), waveDir: getAverageWaveDirection(evening) },
  ]
}

function ForecastOverviewComponent({ spotId, usedFallback: propUsedFallback, activityType, onJoinClick }: ForecastOverviewProps) {
  const { isMobile } = usePlatform()
  const [forecast, setForecast] = useState<ForecastDay[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedDay, setExpandedDay] = useState<number | null>(null)
  const [hoveredHour, setHoveredHour] = useState<HourlyForecast | null>(null)
  const [spotLocation, setSpotLocation] = useState<{ latitude: number; longitude: number; name: string } | null>(null)
  const [isClient, setIsClient] = useState(false)
  const { preferences } = usePreferences()
  const { user } = useAuth()
  const { profile } = useProfile()

  useEffect(() => { setIsClient(true) }, [])

  // Fetch spot location and forecast
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const supabase = createClient()
        if (!supabase) return

        const { data: spotData } = await supabase.from("surf_spots").select("latitude, longitude, name").eq("id", spotId).single()
        if (spotData) {
          setSpotLocation({ latitude: Number(spotData.latitude), longitude: Number(spotData.longitude), name: spotData.name || "Spot" })
        }

        const res = await fetch(`/api/surf-forecast?spotId=${spotId}`)
        const result = await res.json()
        if (result.success && result.data && Array.isArray(result.data.data)) {
          setForecast(result.data.data)
        } else {
          setError(result.error || "Failed to load forecast data")
        }
      } catch (err) {
        setError("Failed to fetch forecast")
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [spotId])

  const toggleExpand = (index: number) => {
    triggerHaptic('impact')
    setExpandedDay(expandedDay === index ? null : index)
    if (expandedDay !== index && forecast[index]?.hourly?.length > 0) {
      setHoveredHour(forecast[index].hourly[12] || forecast[index].hourly[0])
    }
  }

  const formatDate = (dateStr: string, index?: number) => {
    const date = new Date(dateStr)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const compareDate = new Date(date)
    compareDate.setHours(0, 0, 0, 0)

    if (compareDate.getTime() === today.getTime()) return "Today"
    return date.toLocaleDateString("en-US", { weekday: "short", month: "numeric", day: "numeric" })
  }

  const getWindArrow = (degrees: number) => (
    <div style={{ transform: `rotate(${degrees}deg)` }} className="inline-block">
      <ArrowDown className="w-3 h-3" />
    </div>
  )

  if (loading) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide">
        <div className="flex gap-3 px-6">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="min-w-[160px] h-[200px] rounded-[2.5rem] bg-slate-100 dark:bg-slate-800 animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (error || forecast.length === 0) {
    return <div className="p-6 text-center text-slate-400 text-sm">{error || "No forecast available."}</div>
  }

  // Main Version (Unified Premium Design)
  return (
    <div className="space-y-6">
      <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide overflow-y-visible">
        <div className="flex gap-3 px-1">
          {forecast.map((day, index) => {
            const periods = day.hourly ? getTimePeriods(day.hourly) : []
            const avgPeriod = day.hourly ? getAveragePeriod(day.hourly.filter((h) => h.hour >= 6 && h.hour <= 18)) : 0
            
            // Tiered access logic: 
            // - Guests/Free users see only first 2 days
            // - Plus members see all 10 days
            const isPlus = profile?.subscription_status === 'active' || 
                           profile?.subscription_tier === 'plus_lifetime' || 
                           profile?.subscription_tier === 'plus_yearly' || 
                           profile?.subscription_tier === 'free_lifetime_earlybird'
            
            const shouldBlur = !isPlus && index >= 2

            return (
              <div key={day.date} className="relative flex-shrink-0 w-[160px]">
                <Card 
                  className={cn(
                    "w-full bg-white dark:bg-slate-900 border-[1.5px] rounded-[2rem] overflow-hidden flex flex-col shadow-sm transition-all hover:shadow-md active:scale-[0.98]",
                    expandedDay === index ? "border-blue-500 ring-2 ring-blue-500/20 shadow-lg" : "border-slate-100 dark:border-slate-800",
                    shouldBlur && "blur-[6px] grayscale-[0.5] pointer-events-none"
                  )}
                >
                  <div className="p-5 flex flex-col items-center gap-1 cursor-pointer" onClick={() => toggleExpand(index)}>
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">{formatDate(day.date, index)}</span>
                    <div className="text-center text-xs leading-tight text-slate-900 dark:text-white font-bold flex items-center gap-1.5 mb-2">
                      <span className="text-xl">{day.weatherIcon}</span>
                      {convertTemperatureString(day.temperatureDisplay, preferences.temperatureUnit)}
                    </div>
                    <div className="flex flex-col items-center mb-2">
                      <span className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tighter italic leading-none">
                        {convertWaveHeight(day.waveHeight, preferences.unitSystem)}
                      </span>
                      {avgPeriod > 0 && (
                        <div className="flex items-center gap-1 mt-1 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-full">
                          <Clock className="w-2.5 h-2.5 text-blue-500" />
                          <span className="text-[9px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest">{avgPeriod}s</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Activity Quality Bars */}
                    {periods.length === 4 && (
                      <div className="space-y-2 w-full mt-2 bg-slate-50/50 dark:bg-slate-800/30 p-2 rounded-2xl border border-slate-100/50 dark:border-slate-800/50">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px]">🏄</span>
                          <div className="flex gap-1 h-1 flex-1">
                            {periods.map((p, pIdx) => (
                              <div key={pIdx} className="flex-1 rounded-full" style={{ backgroundColor: getQualityColor(p.quality) }} />
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px]">🪁</span>
                          <div className="flex gap-1 h-1 flex-1">
                            {periods.map((p, pIdx) => (
                              <div key={pIdx} className="flex-1 rounded-full" style={{ backgroundColor: getQualityColor(p.kiteQuality || 'poor') }} />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); toggleExpand(index); }} 
                    className={cn(
                      "w-full h-10 flex items-center justify-center border-t transition-colors",
                      expandedDay === index 
                        ? "bg-blue-600 border-blue-600 text-white" 
                        : "bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-800 text-slate-400 hover:text-blue-500"
                    )}
                  >
                    {expandedDay === index ? <ChevronUp className="w-5 h-5 animate-bounce-subtle" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                </Card>
                {shouldBlur && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-auto z-20">
                    <Button 
                      size="sm" 
                      onClick={() => { triggerHaptic('success'); onJoinClick?.(); }}
                      className="h-10 px-6 text-[10px] font-black uppercase tracking-widest bg-blue-600 hover:bg-blue-500 text-white rounded-2xl shadow-xl shadow-blue-600/20 active:scale-95 transition-all group"
                    >
                      <span>Join</span>
                      <ChevronRight className="w-3 h-3 ml-1 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Expanded Forecast Detail (Unified) */}
      {expandedDay !== null && forecast[expandedDay] && (
        <div className="space-y-6 animate-in fade-in slide-in-from-top-4 duration-500">
          <Card className="p-8 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border border-slate-100 dark:border-slate-800 rounded-[3rem] shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-600/20 rotate-3">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tighter italic leading-none">{formatDate(forecast[expandedDay].date, expandedDay)}</h3>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1 italic">Complete Hourly Ocean Intelligence</p>
                </div>
              </div>
              <button 
                onClick={() => setExpandedDay(null)} 
                className="w-10 h-10 bg-slate-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700 transition-all active:scale-90"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Left Column: Map and Summary Stats */}
              <div className="lg:col-span-4 space-y-6">
                {isClient && spotLocation && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between px-1">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                        <h4 className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-[0.2em] italic">Ocean Flow</h4>
                      </div>
                      <ForecastInfoPopup />
                    </div>
                    <div className="h-[300px] w-full bg-slate-50 dark:bg-slate-950 rounded-[2.5rem] overflow-hidden border border-slate-100 dark:border-slate-800 relative shadow-inner">
                      {typeof window !== "undefined" && (
                        <ForecastMapVisualization latitude={spotLocation.latitude} longitude={spotLocation.longitude} hourlyData={hoveredHour} spotName={spotLocation.name} />
                      )}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-[2rem] p-5 border border-slate-100 dark:border-slate-800 transition-all hover:border-blue-500/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Wind className="w-4 h-4 text-blue-500" />
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Max Wind</span>
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-3xl font-black text-slate-900 dark:text-white italic tracking-tighter">{Math.round(Math.max(...forecast[expandedDay].hourly.map(h => h.windSpeed)))}</span>
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{preferences.unitSystem === 'metric' ? 'km/h' : 'mph'}</span>
                    </div>
                  </div>
                  <div className="bg-slate-50 dark:bg-slate-800/50 rounded-[2rem] p-5 border border-slate-100 dark:border-slate-800 transition-all hover:border-purple-500/30">
                    <div className="flex items-center gap-2 mb-3">
                      <Waves className="w-4 h-4 text-purple-500" />
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Avg Period</span>
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-3xl font-black text-slate-900 dark:text-white italic tracking-tighter">{Math.round(forecast[expandedDay].hourly.reduce((acc, h) => acc + h.wavePeriod, 0) / forecast[expandedDay].hourly.length)}</span>
                      <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">sec</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Charts and Timeline */}
              <div className="lg:col-span-8 space-y-8">
                {/* Quality Timelines */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Surf Quality */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between px-1">
                      <h4 className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-[0.2em] italic flex items-center gap-2">
                        <span className="text-base">🏄</span> Surf Potential
                      </h4>
                    </div>
                    <div className="bg-slate-50/50 dark:bg-slate-800/30 rounded-[2rem] p-6 border border-slate-100 dark:border-slate-800">
                      <div className="flex items-end justify-between gap-1.5 h-10 mb-4 px-1">
                        {forecast[expandedDay].hourly.filter(h => h.hour >= 7 && h.hour <= 22).map((hour, hIdx) => (
                          <div key={hIdx} className="flex-1 flex flex-col items-center group relative cursor-pointer" onClick={() => { setHoveredHour(hour); triggerHaptic('selection'); }}>
                            <div className={cn(
                              "w-full rounded-full transition-all duration-300",
                              hoveredHour?.hour === hour.hour ? "h-4 ring-2 ring-slate-900 dark:ring-white ring-offset-2 dark:ring-offset-slate-900" : "h-2 hover:h-3"
                            )} style={{ backgroundColor: getQualityColor(hour.quality) }} />
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between px-1">
                        {[7, 12, 17, 22].map((h) => (
                          <div key={h} className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{h === 12 ? '12pm' : h > 12 ? `${h-12}pm` : `${h}am`}</div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Kite Quality */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between px-1">
                      <h4 className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-[0.2em] italic flex items-center gap-2">
                        <span className="text-base">🪁</span> Kite Conditions
                      </h4>
                    </div>
                    <div className="bg-slate-50/50 dark:bg-slate-800/30 rounded-[2rem] p-6 border border-slate-100 dark:border-slate-800">
                      <div className="flex items-end justify-between gap-1.5 h-10 mb-4 px-1">
                        {forecast[expandedDay].hourly.filter(h => h.hour >= 7 && h.hour <= 22).map((hour, hIdx) => {
                          const kiteQuality = hour.kiteQuality || 'poor'
                          return (
                            <div key={hIdx} className="flex-1 flex flex-col items-center group relative cursor-pointer" onClick={() => { setHoveredHour(hour); triggerHaptic('selection'); }}>
                              <div className={cn(
                                "w-full rounded-full transition-all duration-300",
                                hoveredHour?.hour === hour.hour ? "h-4 ring-2 ring-slate-900 dark:ring-white ring-offset-2 dark:ring-offset-slate-900" : "h-2 hover:h-3"
                              )} style={{ backgroundColor: kiteQuality.toLowerCase() === 'extreme' ? '#7c2d12' : getQualityColor(kiteQuality) }} />
                            </div>
                          )
                        })}
                      </div>
                      <div className="flex justify-between px-1">
                        {[7, 12, 17, 22].map((h) => (
                          <div key={h} className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{h === 12 ? '12pm' : h > 12 ? `${h-12}pm` : `${h}am`}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Main Charts */}
                <div className="space-y-6">
                  {/* Wave Height Chart */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between px-1">
                      <div className="flex items-center gap-2">
                        <Waves className="w-4 h-4 text-blue-500" />
                        <h4 className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-widest italic">Wave Height & Period</h4>
                      </div>
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{preferences.unitSystem === 'metric' ? 'Meters' : 'Feet'}</span>
                    </div>
                    <div className="h-48 w-full min-h-[192px] bg-slate-50/50 dark:bg-slate-800/30 rounded-[2.5rem] p-6 border border-slate-100 dark:border-slate-800">
                      <ResponsiveContainer width="100%" height="100%" minHeight={144}>
                        <AreaChart syncId="weather-charts" margin={{ top: 10, right: 10, left: 0, bottom: 0 }} onMouseMove={(e: any) => { if (e && e.activePayload && e.activePayload.length) { const data = e.activePayload[0].payload; if (hoveredHour?.hour !== data.hour) setHoveredHour(data); } }} data={forecast[expandedDay].hourly.filter(h => h.hour >= 7 && h.hour <= 22).map(h => ({ ...h, displayWaveHeight: preferences.unitSystem === 'metric' ? h.waveHeight * 0.3048 : h.waveHeight }))}>
                          <defs>
                            <linearGradient id="waveHeightGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/><stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/></linearGradient>
                          </defs>
                          <XAxis dataKey="hour" ticks={[7, 12, 17, 22]} tickFormatter={(h) => h === 12 ? '12pm' : h > 12 ? `${h-12}pm` : `${h}am`} tick={{ fontSize: 9, fontWeight: 'black', fill: '#94a3b8' }} axisLine={false} tickLine={false} interval={0} />
                          <YAxis tick={{ fontSize: 9, fontWeight: 'black', fill: '#94a3b8' }} axisLine={false} tickLine={false} width={40} />
                          {hoveredHour && <ReferenceLine x={hoveredHour.hour} stroke="#3b82f6" strokeWidth={2} strokeDasharray="3 3" />}
                          <RechartsTooltip content={({ active, payload }) => { if (active && payload && payload.length) { const data = payload[0].payload; return (<div className="bg-slate-900/95 backdrop-blur-md text-white p-3 rounded-2xl shadow-2xl border border-slate-800"><p className="text-[10px] font-black uppercase tracking-widest mb-1 italic">{data.time}</p><p className="text-lg font-black italic">{data.displayWaveHeight.toFixed(1)}{preferences.unitSystem === 'metric' ? 'm' : 'ft'}</p><p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{data.wavePeriod}s Period</p></div>) } return null; }} />
                          <Area type="monotone" dataKey="displayWaveHeight" stroke="#3b82f6" strokeWidth={4} fill="url(#waveHeightGradient)" dot={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Wind Speed Chart */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between px-1">
                      <div className="flex items-center gap-2">
                        <Wind className="w-4 h-4 text-emerald-500" />
                        <h4 className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-widest italic">Wind Speed</h4>
                      </div>
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{preferences.unitSystem === 'metric' ? 'KM/H' : 'MPH'}</span>
                    </div>
                    <div className="h-48 w-full min-h-[192px] bg-slate-50/50 dark:bg-slate-800/30 rounded-[2.5rem] p-6 border border-slate-100 dark:border-slate-800">
                      <ResponsiveContainer width="100%" height="100%" minHeight={144}>
                        <AreaChart syncId="weather-charts" margin={{ top: 10, right: 10, left: 0, bottom: 0 }} onMouseMove={(e: any) => { if (e && e.activePayload && e.activePayload.length) { const data = e.activePayload[0].payload; if (hoveredHour?.hour !== data.hour) setHoveredHour(data); } }} data={forecast[expandedDay].hourly.filter(h => h.hour >= 7 && h.hour <= 22).map(h => ({ ...h, displayWindSpeed: preferences.unitSystem === 'metric' ? h.windSpeed * 1.60934 : h.windSpeed }))}>
                          <defs>
                            <linearGradient id="windSpeedGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/><stop offset="95%" stopColor="#10b981" stopOpacity={0}/></linearGradient>
                          </defs>
                          <XAxis dataKey="hour" ticks={[7, 12, 17, 22]} tickFormatter={(h) => h === 12 ? '12pm' : h > 12 ? `${h-12}pm` : `${h}am`} tick={{ fontSize: 9, fontWeight: 'black', fill: '#94a3b8' }} axisLine={false} tickLine={false} interval={0} />
                          <YAxis tick={{ fontSize: 9, fontWeight: 'black', fill: '#94a3b8' }} axisLine={false} tickLine={false} width={40} />
                          {hoveredHour && <ReferenceLine x={hoveredHour.hour} stroke="#10b981" strokeWidth={2} strokeDasharray="3 3" />}
                          <RechartsTooltip content={({ active, payload }) => { if (active && payload && payload.length) { const data = payload[0].payload; return (<div className="bg-slate-900/95 backdrop-blur-md text-white p-3 rounded-2xl shadow-2xl border border-slate-800"><p className="text-[10px] font-black uppercase tracking-widest mb-1 italic">{data.time}</p><p className="text-lg font-black italic">{Math.round(data.displayWindSpeed)}{preferences.unitSystem === 'metric' ? 'km/h' : 'mph'}</p><p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{data.windDirection} Wind</p></div>) } return null; }} />
                          <Area type="monotone" dataKey="displayWindSpeed" stroke="#10b981" strokeWidth={4} fill="url(#windSpeedGradient)" dot={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Swell Height Chart */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between px-1">
                      <div className="flex items-center gap-2">
                        <Waves className="w-4 h-4 text-indigo-500" />
                        <h4 className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-widest italic">Swell Height</h4>
                      </div>
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{preferences.unitSystem === 'metric' ? 'Meters' : 'Feet'}</span>
                    </div>
                    <div className="h-48 w-full min-h-[192px] bg-slate-50/50 dark:bg-slate-800/30 rounded-[2.5rem] p-6 border border-slate-100 dark:border-slate-800">
                      <ResponsiveContainer width="100%" height="100%" minHeight={144}>
                        <AreaChart syncId="weather-charts" margin={{ top: 10, right: 10, left: 0, bottom: 0 }} onMouseMove={(e: any) => { if (e && e.activePayload && e.activePayload.length) { const data = e.activePayload[0].payload; if (hoveredHour?.hour !== data.hour) setHoveredHour(data); } }} data={forecast[expandedDay].hourly.filter(h => h.hour >= 7 && h.hour <= 22).map(h => ({ ...h, displaySwellHeight: preferences.unitSystem === 'metric' ? (h.swellHeight || h.waveHeight) * 0.3048 : (h.swellHeight || h.waveHeight) }))}>
                          <defs>
                            <linearGradient id="swellHeightGradient" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/><stop offset="95%" stopColor="#6366f1" stopOpacity={0}/></linearGradient>
                          </defs>
                          <XAxis dataKey="hour" ticks={[7, 12, 17, 22]} tickFormatter={(h) => h === 12 ? '12pm' : h > 12 ? `${h-12}pm` : `${h}am`} tick={{ fontSize: 9, fontWeight: 'black', fill: '#94a3b8' }} axisLine={false} tickLine={false} interval={0} />
                          <YAxis tick={{ fontSize: 9, fontWeight: 'black', fill: '#94a3b8' }} axisLine={false} tickLine={false} width={40} />
                          {hoveredHour && <ReferenceLine x={hoveredHour.hour} stroke="#6366f1" strokeWidth={2} strokeDasharray="3 3" />}
                          <RechartsTooltip content={({ active, payload }) => { if (active && payload && payload.length) { const data = payload[0].payload; return (<div className="bg-slate-900/95 backdrop-blur-md text-white p-3 rounded-2xl shadow-2xl border border-slate-800"><p className="text-[10px] font-black uppercase tracking-widest mb-1 italic">{data.time}</p><p className="text-lg font-black italic">{data.displaySwellHeight.toFixed(1)}{preferences.unitSystem === 'metric' ? 'm' : 'ft'}</p><p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{data.wavePeriod}s Period</p></div>) } return null; }} />
                          <Area type="monotone" dataKey="displaySwellHeight" stroke="#6366f1" strokeWidth={4} fill="url(#swellHeightGradient)" dot={false} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Tide Chart */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between px-1">
                      <div className="flex items-center gap-2">
                        <Waves className="w-4 h-4 text-blue-400" />
                        <h4 className="text-[10px] font-black text-slate-900 dark:text-white uppercase tracking-widest italic">Tide Predictions</h4>
                      </div>
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{preferences.unitSystem === 'metric' ? 'Meters' : 'Feet'}</span>
                    </div>
                    <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm">
                      <TideGraph 
                        data={forecast[expandedDay].hourly.filter(h => h.hour >= 7 && h.hour <= 22).map(h => ({ 
                          time: h.timestamp || h.time, 
                          hour: h.hour,
                          tideHeight: h.tideHeight || 0 
                        }))} 
                        extremes={forecast[expandedDay].tides}
                        unitSystem={preferences.unitSystem}
                        hoveredHour={hoveredHour?.hour}
                        onHoverChange={(hour) => {
                          if (hour === null) {
                            setHoveredHour(null);
                          } else {
                            const matchingHour = forecast[expandedDay].hourly.find(h => h.hour === hour);
                            if (matchingHour) setHoveredHour(matchingHour);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Hourly Table (Unified Desktop Style) */}
            <div className="mt-12 space-y-6">
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center">
                    <Clock className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-black text-slate-900 dark:text-white uppercase tracking-tight italic">Hourly Ocean Matrix</h4>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Precise conditions for every window</p>
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto -mx-8 px-8 scrollbar-hide">
                <table className="w-full text-left border-separate border-spacing-y-2 min-w-[800px]">
                  <thead>
                    <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest italic">
                      <th className="px-6 py-2">Time</th>
                      <th className="px-6 py-2">Condition</th>
                      <th className="px-6 py-2">Temp</th>
                      <th className="px-6 py-2">Wave</th>
                      <th className="px-6 py-2">Period</th>
                      <th className="px-6 py-2 text-center">Direction</th>
                      <th className="px-6 py-2">Wind</th>
                      <th className="px-6 py-2 text-center">Tide</th>
                      <th className="px-6 py-2 text-center">Surf</th>
                      <th className="px-6 py-2 text-center">Kite</th>
                    </tr>
                  </thead>
                  <tbody>
                    {forecast[expandedDay].hourly.filter(h => h.hour >= 7 && h.hour <= 22).map((hour, i) => (
                      <tr 
                        key={i} 
                        className={cn(
                          "transition-all cursor-pointer group/row",
                          hoveredHour?.hour === hour.hour 
                            ? "bg-blue-600 shadow-xl shadow-blue-600/20 translate-x-1" 
                            : "bg-slate-50/50 dark:bg-slate-800/30 hover:bg-slate-100 dark:hover:bg-slate-800/50"
                        )} 
                        onClick={() => { setHoveredHour(hour); triggerHaptic('selection'); }}
                      >
                        <td className={cn("px-6 py-4 first:rounded-l-[1.5rem] font-black text-xs uppercase tracking-widest italic", hoveredHour?.hour === hour.hour ? "text-white" : "text-slate-900 dark:text-white")}>
                          {hour.time.split(' ')[0]} {hour.time.split(' ')[1]}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xl">{hour.weatherIcon}</span>
                        </td>
                        <td className={cn("px-6 py-4 font-bold text-xs", hoveredHour?.hour === hour.hour ? "text-blue-100" : "text-slate-500")}>
                          {convertTemperatureString(hour.temperatureDisplay, preferences.temperatureUnit)}
                        </td>
                        <td className={cn("px-6 py-4 font-black text-sm italic", hoveredHour?.hour === hour.hour ? "text-white" : "text-slate-900 dark:text-white")}>
                          {convertWaveHeight(hour.waveHeightDisplay, preferences.unitSystem)}
                        </td>
                        <td className={cn("px-6 py-4 font-bold text-xs", hoveredHour?.hour === hour.hour ? "text-blue-100" : "text-slate-500")}>
                          {hour.wavePeriod}s
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <div className={cn("transition-transform", hoveredHour?.hour === hour.hour ? "text-white" : "text-slate-400")} style={{ transform: `rotate(${hour.waveDirectionDeg}deg)` }}>
                              <Navigation className="w-3 h-3 fill-current" />
                            </div>
                            <span className={cn("text-[8px] font-black uppercase tracking-widest", hoveredHour?.hour === hour.hour ? "text-blue-100" : "text-slate-500")}>{hour.waveDirection}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <span className={cn("font-black text-sm italic", hoveredHour?.hour === hour.hour ? "text-white" : "text-slate-900 dark:text-white")}>
                              {convertWindSpeed(hour.windSpeedDisplay, preferences.unitSystem)}
                            </span>
                            <div className={cn("transition-transform", hoveredHour?.hour === hour.hour ? "text-white" : "text-slate-400")} style={{ transform: `rotate(${hour.windDirectionDeg}deg)` }}>
                              <ArrowDown className="w-3 h-3" />
                            </div>
                          </div>
                        </td>
                        <td className={cn("px-6 py-4 text-center font-bold text-xs", hoveredHour?.hour === hour.hour ? "text-blue-100" : "text-slate-500")}>
                          <div className="flex flex-col items-center">
                            <span>{hour.tideHeight !== undefined ? (preferences.unitSystem === 'metric' ? `${hour.tideHeight.toFixed(1)}m` : `${(hour.tideHeight * 3.28084).toFixed(1)}ft`) : '--'}</span>
                            {hour.tideTrend && (
                              <span className="text-[8px] font-black uppercase tracking-widest mt-0.5 opacity-70">
                                {hour.tideTrend === 'rising' ? '↗' : hour.tideTrend === 'falling' ? '↘' : '→'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center items-center gap-2">
                            <span className="text-xs">🏄</span>
                            <div className="w-2 h-2 rounded-full shadow-sm" style={{ backgroundColor: getQualityColor(hour.quality) }} />
                          </div>
                        </td>
                        <td className="px-6 py-4 last:rounded-r-[1.5rem] text-center">
                          <div className="flex justify-center items-center gap-2">
                            <span className="text-xs">🪁</span>
                            <div className="w-2 h-2 rounded-full shadow-sm" style={{ backgroundColor: hour.kiteQuality?.toLowerCase() === 'extreme' ? '#7c2d12' : getQualityColor(hour.kiteQuality || 'poor') }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

export const ForecastOverview = React.memo(ForecastOverviewComponent, (prevProps, nextProps) => {
  return prevProps.spotId === nextProps.spotId && prevProps.usedFallback === nextProps.usedFallback && prevProps.activityType === nextProps.activityType
})
